import { PlusOutlined, UndoOutlined } from "@ant-design/icons";
import { Button, DatePicker, Select } from "antd";
import SelectInfinity from "components/select-infinity/SelectInfinity";
import { COMMON_STATUS } from "constants/constants";
import CheckAccess from "hoc/CheckAccess";
import { Ref, useImperativeHandle, useState } from "react";
import { translate } from "utils";
import { HotSaleQueryParams, MerchantQuery } from "./type";
import { queryDefault, setHotSaleFilter } from "store/filter/index.slice";
import moment, { Moment } from "moment";
import { getMerchantsAPI } from "service/apis";
import { Merchant } from "features/merchant/types";
import { SEARCH_KEY } from "./constant";
import { useDispatch } from "react-redux";
import { PERMISSION } from "../../../constants/permission";

export interface FormatQueryRef {
	handleFormatQuery: (query: HotSaleQueryParams) => HotSaleQueryParams;
}

export interface SearchProps {
	query: HotSaleQueryParams;
	setQuery: React.Dispatch<React.SetStateAction<HotSaleQueryParams>>;
	getList: (newQuery: HotSaleQueryParams, callback?: () => void) => void;
	setOpenCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
	fowardRef: Ref<FormatQueryRef>;
}

const Search = ({
	query,
	setQuery,
	getList,
	setOpenCreateModal,
	fowardRef,
}: SearchProps) => {
	const dispatch = useDispatch();
	const { startDate, endDate, status } = query;
	const [merchantList, setMerchantList] = useState<Merchant[]>([]);
	const [merchantQuery, setMerchantQuery] = useState<MerchantQuery>({
		...queryDefault,
		status: COMMON_STATUS.enable.value,
	});

	useImperativeHandle(fowardRef, () => ({
		handleFormatQuery,
	}));

	const handleFormatQuery = (query: HotSaleQueryParams) => {
		if (typeof query.merchant === "string") {
			const keptQuery = { ...query };
			const findMerchantItem = merchantList.find(
				(merchant: Merchant) =>
					`${merchant.fullName} ${merchant.username}` === query.merchant,
			);
			return {
				...keptQuery,
				merchantId: findMerchantItem?.id,
			};
		} else return query;
	};

	const handleChangeKeyword = (key: string, value: string) => {
		const newQuery = {
			...query,
			[key]: value,
			skip: 0,
		};
		if (key === SEARCH_KEY.MERCHANT.lower) {
			const callback = () => {
				setQuery(newQuery);
				dispatch(setHotSaleFilter(newQuery));
			};
			getList(handleFormatQuery(newQuery), callback);

			if (!value && merchantQuery.fullName) {
				const newMerchantQuery = {
					...merchantQuery,
					fullName: null,
					skip: 0,
				};
				setMerchantQuery(newMerchantQuery);
			}
		} else getList(handleFormatQuery(newQuery));
	};

	const handleReset = () => {
		getList(queryDefault);
	};

	const handleChangeRangeDate = (
		values: [Moment | null, Moment | null] | null,
	) => {
		const [start, end] = values || [];

		const newQuery = {
			...query,
			startDate: start?.startOf("day")?.toISOString(),
			endDate: end?.endOf("day")?.toISOString(),
			skip: 0,
		};
		getList(handleFormatQuery(newQuery));
	};

	const handleUpdateMerchantQuery = (fullName: string) => {
		const newMerchantQuery = {
			...merchantQuery,
			fullName: fullName || null,
			skip: 0,
		};
		setMerchantQuery(newMerchantQuery);
	};

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
			<div>
				<p>{translate(SEARCH_KEY.MERCHANT.lower)}</p>
				<SelectInfinity
					showSearch
					allowClear
					haveAllValue
					className="w-full"
					query={merchantQuery}
					setQuery={setMerchantQuery}
					renderList={merchantList}
					setRenderList={setMerchantList}
					fetchMoreFunc={getMerchantsAPI}
					value={query.merchant || ""}
					onChange={merchant =>
						handleChangeKeyword(SEARCH_KEY.MERCHANT.lower, merchant)
					}
					onSearch={e => handleUpdateMerchantQuery(e)}
					optionRender={(item: Merchant) => (
						<Select.Option
							value={`${item.fullName} ${item.username}`}
							key={item.id}
						>
							<p>{item.fullName}</p>
							<span className="text-sm text-[#95a5a6]">{item.username}</span>
						</Select.Option>
					)}
				/>
			</div>
			<div>
				<p>{translate(SEARCH_KEY.STATUS.upper)}</p>
				<Select
					placeholder={translate("All")}
					className="w-full"
					onChange={(value: string) =>
						handleChangeKeyword(SEARCH_KEY.STATUS.lower, value)
					}
					value={status || null}
					allowClear
				>
					<Select.Option value={null}>{translate("All")}</Select.Option>
					<Select.Option value={COMMON_STATUS.enabled.value}>
						{translate(COMMON_STATUS.enable.label)}
					</Select.Option>
					<Select.Option value={COMMON_STATUS.disabled.value}>
						{translate(COMMON_STATUS.disable.label)}
					</Select.Option>
				</Select>
			</div>
			<div>
				<p>{translate(SEARCH_KEY.START_DATE.upper)}</p>
				<DatePicker.RangePicker
					className="w-full"
					value={[
						startDate ? moment(startDate) : null,
						endDate ? moment(endDate) : null,
					]}
					format="YYYY-MM-DD"
					allowEmpty={[true, true]}
					allowClear
					onChange={handleChangeRangeDate}
				/>
			</div>
			<div className="flex space-x-4">
				<Button
					icon={<UndoOutlined />}
					className="flex items-center self-end rounded"
					onClick={handleReset}
				>
					{translate("Reset")}
				</Button>
				<CheckAccess perms={[PERMISSION.HOTSALE.UPDATE]}>
					<Button
						type="primary"
						className="bg-[#84AD2B] hover:bg-[#84AD2B] focus:bg-[#84AD2B] flex items-center self-end rounded border-0"
						icon={<PlusOutlined />}
						htmlType="submit"
						onClick={() => setOpenCreateModal(true)}
					>
						{translate("Create")}
					</Button>
				</CheckAccess>
			</div>
		</div>
	);
};

export default Search;
